import type { ReactNode } from 'react';
import { Marca } from '../marca';
import { BotaoDeTema } from '../BotaoDeTema';

/**
 * A moldura da entrada — a faixa da marca (onda 284).
 *
 * ── De onde ela veio ─────────────────────────────────────────────────
 *
 * Em 03/10/2026 a revisão de design da entrada (doc
 * `000-ideia-portal-entrada-email-carbon`) encontrou o painel ao lado do
 * formulário falando outra língua: carrossel de frases trocando sozinho,
 * clarão em gradiente, selos em pílula — três coisas que o Carbon não usa.
 * Em 06/10 o Humberto viu três molduras desenhadas no quadro «Portal do
 * cliente · opções Carbon Testesolo» e escolheu a C: a faixa da marca à
 * esquerda e o formulário à direita.
 *
 * ── Por que a faixa é marrom no claro ────────────────────────────────
 *
 * Porque é onde a Testesolo assina. O arquivo de tokens lista «o painel
 * da entrada» entre os lugares do marrom — identidade, não ação. No
 * escuro ela vira grafite e a marca vira a branca, sozinhas, pelos
 * tokens (`--ts-marrom-escuro`, `--ts-marca-*`).
 *
 * ── No celular ───────────────────────────────────────────────────────
 *
 * A faixa vira a barra de 48 px que todas as outras telas do portal têm,
 * com a marca e o nome do produto; o texto e as garantias somem, e o
 * formulário vem logo abaixo. É a mesma tela nas três molduras que foram
 * desenhadas: no celular elas eram iguais.
 *
 * A moldura serve às duas portas: a de pedir código (`Entrar`) e a do link
 * do e-mail (`Acesso`). Quem chega por qualquer uma vê a mesma casa.
 */

/**
 * As garantias.
 *
 * A regra é a dos selos de antes: eles descrevem **o que o sistema faz**,
 * nunca o que o laboratório é. A Testesolo não é acreditada (confirmado
 * em 08/09/2026), e um selo de norma numa tela pública lê como declaração
 * de acreditação. Estas quatro qualquer pessoa confere abrindo o
 * documento.
 */
const GARANTIAS = [
  'Trilha de auditoria em cada registro',
  'Calibração interna do equipamento registrada',
  'Norma vigente citada no ensaio',
  'Laudo com QR de autenticidade',
];

export function MolduraDaEntrada({ children }: { children: ReactNode }) {
  return (
    <div className="entrada">
      <aside className="entrada-faixa" aria-label="Testesolo Engenharia Geotécnica">
        <Marca />
        <div className="entrada-faixa-corpo">
          <p className="entrada-faixa-titulo">Portal do cliente</p>
          <p className="entrada-faixa-texto">
            Laudos, relatórios de recebimento e ensaios executados de cada
            ordem de serviço da sua obra — sempre na revisão que vale.
          </p>
          <ul className="entrada-garantias">
            {GARANTIAS.map((g) => (
              <li key={g}>
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
                     fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12l5 5 9-10" />
                </svg>
                {g}
              </li>
            ))}
          </ul>
          <p className="entrada-faixa-rodape">
            <span>Testesolo Engenharia Geotécnica</span>
            <span>Blumenau/SC</span>
          </p>
        </div>
      </aside>

      <div className="entrada-lado">
        <div className="entrada-topo"><BotaoDeTema /></div>
        <main className="entrada-form">{children}</main>
      </div>
    </div>
  );
}

/**
 * A régua dos dois passos. A régua é desenho; quem diz em que passo a
 * pessoa está é o texto embaixo dela (achado P2 #6 de 03/10).
 */
export function Passos({ atual }: { atual: 1 | 2 }) {
  return (
    <div className="passos">
      <div className="passos-regua" aria-hidden="true">
        <span className={atual === 1 ? 'atual' : 'feito'} />
        <span className={atual === 2 ? 'atual' : undefined} />
      </div>
      <p className="passos-rotulo">
        Passo {atual} de 2 · {atual === 1 ? 'E-mail' : 'Código'}
      </p>
    </div>
  );
}

type TipoDeAviso = 'ok' | 'atencao' | 'erro';

const ICONE_DO_AVISO: Record<TipoDeAviso, ReactNode> = {
  ok: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.8 2.8 5.7-5.8" />
    </>
  ),
  atencao: (
    <>
      <path d="M12 3.5 2.5 20h19L12 3.5z" />
      <path d="M12 10v4.5M12 17.3v.2" />
    </>
  ),
  erro: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.3v.2" />
    </>
  ),
};

/**
 * A notificação do Carbon: a camada da cor do estado, a régua à esquerda
 * e um ícone — a cor nunca fala sozinha. O que é bom anuncia com calma
 * (`status`); o que pede ação interrompe (`alert`).
 */
export function Notificacao(
  { tipo, titulo, children }: {
    tipo: TipoDeAviso; titulo?: string; children?: ReactNode;
  },
) {
  return (
    <div className={`notificacao ${tipo}`} role={tipo === 'ok' ? 'status' : 'alert'}>
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"
           fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICONE_DO_AVISO[tipo]}
      </svg>
      <div>
        {titulo && <strong>{titulo}</strong>}
        {titulo && children ? ' ' : null}
        {children}
      </div>
    </div>
  );
}

/** A seta do botão primário do Carbon — rótulo à esquerda, seta à direita. */
export function Seta() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** O ícone que gira enquanto o servidor responde. */
export function Girando() {
  return (
    <svg className="girando" width="16" height="16" viewBox="0 0 16 16"
         aria-hidden="true" fill="none" strokeWidth="2">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 1.5a6.5 6.5 0 0 1 6.5 6.5" />
    </svg>
  );
}

/** O telefone da Testesolo, que toca no celular. */
export function Telefone() {
  return <a href="tel:+554730372817">(47) 3037-2817</a>;
}

/** A linha de ajuda ao pé do formulário. */
export function Ajuda({ children }: { children: ReactNode }) {
  return <p className="entrada-ajuda">{children}</p>;
}
